import React, { useState, useRef, useEffect } from 'react';
import { 
  Settings2, ChevronDown, Check, Sparkles, Shield, AlertTriangle, 
  RotateCcw, Info, Compass, Building2, Truck, Award
} from 'lucide-react';
import { VedicShuddhiOptions } from '../types/muhurta';
import { DEFAULT_SHUDDHI_OPTIONS, SHUDDHI_PRESETS } from '../utils/muhurtaEngine';

interface VedicShuddhiDropdownProps {
  options: VedicShuddhiOptions;
  onChange: (newOptions: VedicShuddhiOptions) => void;
  lang?: 'en' | 'hi';
  compact?: boolean;
}

export const VedicShuddhiDropdown: React.FC<VedicShuddhiDropdownProps> = ({
  options,
  onChange,
  lang = 'en',
  compact = false
}) => {
  const isHi = lang === 'hi';
  const [isOpen, setIsOpen] = useState(false);
  const [draftOptions, setDraftOptions] = useState<VedicShuddhiOptions>(options);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Sync draftOptions with prop when dropdown is closed or prop updates
  useEffect(() => {
    if (!isOpen) {
      setDraftOptions(options);
    }
  }, [options, isOpen]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Count active rules
  const activeCount = Object.values(options).filter(Boolean).length;
  const activeDraftCount = Object.values(draftOptions).filter(Boolean).length;
  const hasPendingChanges = JSON.stringify(draftOptions) !== JSON.stringify(options);

  // Check which preset currently matches the draft
  const matchedPresetKey = Object.keys(SHUDDHI_PRESETS).find(key => {
    const pOpts = SHUDDHI_PRESETS[key].options;
    return (Object.keys(pOpts) as (keyof VedicShuddhiOptions)[]).every(
      k => pOpts[k] === draftOptions[k]
    );
  });

  const handleToggle = (key: keyof VedicShuddhiOptions) => {
    setDraftOptions(prev => {
      const next = { ...prev, [key]: !prev[key] };
      // Mutual exclusivity between Sthira and Chara
      if (key === 'requireSthiraLagna' && next.requireSthiraLagna) {
        next.requireCharaLagna = false;
      } else if (key === 'requireCharaLagna' && next.requireCharaLagna) {
        next.requireSthiraLagna = false;
      }
      onChange(next);
      return next;
    });
  };

  const handleSelectPreset = (presetKey: string) => {
    if (SHUDDHI_PRESETS[presetKey]) {
      const next = { ...SHUDDHI_PRESETS[presetKey].options };
      setDraftOptions(next);
      onChange(next);
    }
  };

  const handleReset = () => {
    const next = { ...DEFAULT_SHUDDHI_OPTIONS };
    setDraftOptions(next);
    onChange(next);
  };

  const handleApply = () => {
    onChange(draftOptions);
    setIsOpen(false);
  };

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      {/* Space-Saving Trigger Button */}
      <button
        type="button"
        id="vedic-shuddhi-dropdown-btn"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls="vedic-shuddhi-popover"
        aria-label={isHi ? 'वैदिक शुद्धि एवं लग्न नियम' : 'Vedic Shuddhi rules'}
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex items-center gap-2 rounded-xl font-bold transition-all cursor-pointer shadow-2xs border ${
          compact
            ? 'px-2.5 py-1.5 text-xs bg-white hover:bg-astro-card border-astro-border text-astro-primary'
            : 'px-3.5 py-2 text-xs bg-white hover:bg-astro-card border-astro-border text-astro-primary'
        } ${isOpen ? 'ring-2 ring-astro-accent/20 border-astro-accent' : ''}`}
        title={isHi ? 'वैदिक शुद्धि एवं लग्न नियम (स्थिर/चर लग्न, अष्टम शुद्धि आदि)' : 'Configure Vedic Shuddhi rules (Sthira Lagna, Ashtama Shuddhi)'}
      >
        <div className="flex items-center gap-1.5">
          <Settings2 className="w-3.5 h-3.5 text-astro-accent" />
          <span>{isHi ? 'वैदिक शुद्धि नियम' : 'Vedic Shuddhi'}</span>
        </div>

        {/* Status Pill Badge */}
        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-extrabold bg-astro-primary/10 text-astro-primary">
          {options.requireSthiraLagna ? (isHi ? 'स्थिर' : 'Sthira') : options.requireCharaLagna ? (isHi ? 'चर' : 'Chara') : activeCount}
        </span>

        <ChevronDown className={`w-3.5 h-3.5 text-astro-secondary-text transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Floating Popover Panel */}
      {isOpen && (
        <div 
          id="vedic-shuddhi-popover"
          role="dialog"
          aria-labelledby="vedic-shuddhi-dropdown-btn"
          className="absolute right-0 sm:right-auto sm:left-0 mt-2 w-[340px] sm:w-[380px] bg-white rounded-2xl shadow-xl border border-astro-border/80 p-4 z-50 animate-in fade-in zoom-in-95 duration-150"
        >
          {/* Header */}
          <div className="flex items-center justify-between pb-3 border-b border-astro-border/60">
            <div>
              <h4 className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                <Compass className="w-4 h-4 text-astro-accent" />
                <span>{isHi ? 'वैदिक शुद्धि व लग्न मापदंड' : 'Vedic Shuddhi & Lagna Modality'}</span>
              </h4>
              <p className="text-[10px] text-astro-secondary-text mt-0.5">
                {isHi ? 'चिरस्थायी व्यापार, अष्टम शुद्धि व दोष निवारण' : 'Lifelong business stability, 8H shuddhi & Gandanta filters'}
              </p>
            </div>
            <button
              type="button"
              onClick={handleReset}
              className="text-[10px] font-semibold text-astro-secondary-text hover:text-astro-accent flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-astro-card transition-colors cursor-pointer"
              title={isHi ? 'डिफ़ॉल्ट पर रीसेट करें' : 'Reset to default rules'}
            >
              <RotateCcw className="w-3 h-3" />
              <span>{isHi ? 'रीसेट' : 'Reset'}</span>
            </button>
          </div>

          {/* Quick 1-Click Presets */}
          <div className="py-2.5 border-b border-astro-border/50">
            <span className="text-[10px] font-bold text-astro-secondary-text uppercase tracking-wider block mb-1.5">
              {isHi ? 'त्वरित प्रीसेट (1-क्लिक)' : 'Recommended Shastric Presets'}
            </span>
            <div className="grid grid-cols-2 gap-1.5">
              {Object.entries(SHUDDHI_PRESETS).map(([key, preset]) => {
                const isSelected = matchedPresetKey === key;
                const icon = key === 'lifelong_business' 
                  ? <Building2 className="w-3 h-3 text-emerald-600" />
                  : key === 'quick_trade'
                  ? <Truck className="w-3 h-3 text-blue-600" />
                  : key === 'strict_shastric'
                  ? <Award className="w-3 h-3 text-amber-600" />
                  : <Sparkles className="w-3 h-3 text-purple-600" />;

                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handleSelectPreset(key)}
                    className={`flex items-center gap-1.5 p-2 rounded-xl text-left border transition-all cursor-pointer ${
                      isSelected
                        ? 'bg-astro-primary text-white border-astro-primary shadow-xs'
                        : 'bg-astro-card/50 hover:bg-astro-card border-astro-border text-astro-primary'
                    }`}
                  >
                    {icon}
                    <div className="truncate">
                      <div className="text-[11px] font-bold truncate">
                        {isHi ? preset.hiLabel : preset.label}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Granular Toggles */}
          <div className="py-3 space-y-3 max-h-[320px] overflow-y-auto pr-1">
            {/* Category: Lagna Modality & House Occupancies */}
            <div>
              <span className="text-[10px] font-extrabold text-astro-primary uppercase tracking-wider flex items-center gap-1 mb-2">
                <Building2 className="w-3 h-3 text-astro-accent" />
                {isHi ? 'लग्न प्रकृति एवं भाव शुद्धि' : 'Lagna Modality & House Shuddhi'}
              </span>

              <div className="space-y-2">
                {/* Sthira Lagna */}
                <label className="flex items-start justify-between gap-3 p-2 rounded-xl bg-astro-card/30 hover:bg-astro-card/70 border border-astro-border/40 cursor-pointer transition-colors">
                  <div className="space-y-0.5">
                    <div className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                      <span>{isHi ? 'स्थिर लग्न अनिवार्य (Sthira Lagna)' : 'Enforce Sthira (Fixed) Lagna'}</span>
                      <span className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                        {isHi ? 'वृष, सिंह, वृश्चिक, कुंभ' : 'Taurus, Leo, Scorpio, Aquarius'}
                      </span>
                    </div>
                    <p className="text-[10px] text-astro-secondary-text leading-tight">
                      {isHi 
                        ? 'दीर्घकालिक व्यापार, कार्यालय व स्थायी प्रतिष्ठानों के लिए अचल नींव प्रदान करता है।' 
                        : 'Bedrock permanence for lifelong business, incorporation, and real estate.'}
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={draftOptions.requireSthiraLagna}
                    onChange={() => handleToggle('requireSthiraLagna')}
                    className="mt-1 h-4 w-4 rounded text-astro-primary focus:ring-astro-accent border-astro-border cursor-pointer accent-astro-primary"
                  />
                </label>

                {/* Chara Lagna */}
                <label className="flex items-start justify-between gap-3 p-2 rounded-xl bg-astro-card/30 hover:bg-astro-card/70 border border-astro-border/40 cursor-pointer transition-colors">
                  <div className="space-y-0.5">
                    <div className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                      <span>{isHi ? 'चर लग्न अनिवार्य (Chara Lagna)' : 'Enforce Chara (Movable) Lagna'}</span>
                      <span className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-blue-50 text-blue-700 border border-blue-200">
                        {isHi ? 'मेष, कर्क, तुला, मकर' : 'Aries, Cancer, Libra, Capricorn'}
                      </span>
                    </div>
                    <p className="text-[10px] text-astro-secondary-text leading-tight">
                      {isHi 
                        ? 'शीघ्र माल टर्नओवर, वाहन, लॉजिस्टिक्स व त्वरित लाभ के लिए श्रेष्ठ।' 
                        : 'High asset velocity, fleet acquisition, logistics & fast-turnover trade.'}
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={draftOptions.requireCharaLagna}
                    onChange={() => handleToggle('requireCharaLagna')}
                    className="mt-1 h-4 w-4 rounded text-astro-primary focus:ring-astro-accent border-astro-border cursor-pointer accent-astro-primary"
                  />
                </label>

                {/* Ashtama Shuddhi */}
                <label className="flex items-start justify-between gap-3 p-2 rounded-xl bg-astro-card/30 hover:bg-astro-card/70 border border-astro-border/40 cursor-pointer transition-colors">
                  <div className="space-y-0.5">
                    <div className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                      <span>{isHi ? 'अष्टम भाव शुद्धि (Ashtama Shuddhi)' : 'Ashtama Shuddhi (8H Clean)'}</span>
                      <Shield className="w-3 h-3 text-emerald-600" />
                    </div>
                    <p className="text-[10px] text-astro-secondary-text leading-tight">
                      {isHi 
                        ? 'लग्न से आठवें भाव में कोई ग्रह न हो; अप्रत्याशित वित्तीय संकट व कर्ज़ से रक्षा।' 
                        : '8th House from Muhurta Lagna must be vacant to prevent sudden debt & crisis.'}
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={draftOptions.ashtamaShuddhi}
                    onChange={() => handleToggle('ashtamaShuddhi')}
                    className="mt-1 h-4 w-4 rounded text-astro-primary focus:ring-astro-accent border-astro-border cursor-pointer accent-astro-primary"
                  />
                </label>

                {/* Upachaya Malefics */}
                <label className="flex items-start justify-between gap-3 p-2 rounded-xl bg-astro-card/30 hover:bg-astro-card/70 border border-astro-border/40 cursor-pointer transition-colors">
                  <div className="space-y-0.5">
                    <div className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                      <span>{isHi ? 'उपचय भाव में क्रूर ग्रह (Upachaya Strength)' : 'Malefics in Upachaya (3, 6, 11)'}</span>
                    </div>
                    <p className="text-[10px] text-astro-secondary-text leading-tight">
                      {isHi 
                        ? 'सूर्य, मंगल, शनि ३, ६, ११ में हों तो प्रतिस्पर्धियों पर विजय प्राप्त होती है।' 
                        : 'Sun, Mars, Saturn in 3rd/6th/11th house crushes competitive obstacles.'}
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={draftOptions.upachayaMalefics}
                    onChange={() => handleToggle('upachayaMalefics')}
                    className="mt-1 h-4 w-4 rounded text-astro-primary focus:ring-astro-accent border-astro-border cursor-pointer accent-astro-primary"
                  />
                </label>

                {/* Kendra Benefics */}
                <label className="flex items-start justify-between gap-3 p-2 rounded-xl bg-astro-card/30 hover:bg-astro-card/70 border border-astro-border/40 cursor-pointer transition-colors">
                  <div className="space-y-0.5">
                    <div className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                      <span>{isHi ? 'केंद्र में शुभ ग्रह (Kendra Benefics)' : 'Benefics in Kendras (1, 4, 7, 10)'}</span>
                    </div>
                    <p className="text-[10px] text-astro-secondary-text leading-tight">
                      {isHi 
                        ? 'गुरु, शुक्र, बुध केंद्र में हों तो प्रतिष्ठा, संस्थागत विश्वास व दीर्घायु मिलती है।' 
                        : 'Jupiter, Venus, Mercury in Kendras ensures goodwill, trust & protection.'}
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={draftOptions.kendraBenefics}
                    onChange={() => handleToggle('kendraBenefics')}
                    className="mt-1 h-4 w-4 rounded text-astro-primary focus:ring-astro-accent border-astro-border cursor-pointer accent-astro-primary"
                  />
                </label>
              </div>
            </div>

            {/* Category: Sandhis & Toxic Windows */}
            <div>
              <span className="text-[10px] font-extrabold text-astro-primary uppercase tracking-wider flex items-center gap-1 mb-2">
                <AlertTriangle className="w-3 h-3 text-amber-600" />
                {isHi ? 'संधि व महादोष निवारण' : 'Sandhi & Mahadosha Filtering'}
              </span>

              <div className="space-y-2">
                {/* Gandanta Filter */}
                <label className="flex items-start justify-between gap-3 p-2 rounded-xl bg-astro-card/30 hover:bg-astro-card/70 border border-astro-border/40 cursor-pointer transition-colors">
                  <div className="space-y-0.5">
                    <div className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                      <span>{isHi ? 'गंडान्त दोष त्याग (Gandanta Filter)' : 'Filter Gandanta Junctions'}</span>
                    </div>
                    <p className="text-[10px] text-astro-secondary-text leading-tight">
                      {isHi 
                        ? 'रेवती-अश्विनी, आश्लेषा-मघा, ज्येष्ठा-मूल जल-अग्नि संधियों का त्याग।' 
                        : 'Eliminates fatal water-fire transition boundaries (Revati-Ashwini, etc.).'}
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={draftOptions.gandantaFilter}
                    onChange={() => handleToggle('gandantaFilter')}
                    className="mt-1 h-4 w-4 rounded text-astro-primary focus:ring-astro-accent border-astro-border cursor-pointer accent-astro-primary"
                  />
                </label>

                {/* Visha Ghati */}
                <label className="flex items-start justify-between gap-3 p-2 rounded-xl bg-astro-card/30 hover:bg-astro-card/70 border border-astro-border/40 cursor-pointer transition-colors">
                  <div className="space-y-0.5">
                    <div className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                      <span>{isHi ? 'विष घटी त्याग (Visha Ghati)' : 'Exclude Visha Ghati Windows'}</span>
                    </div>
                    <p className="text-[10px] text-astro-secondary-text leading-tight">
                      {isHi 
                        ? 'प्रत्येक नक्षत्र की विषैली ४ घटी (९६ मिनट) का परित्याग।' 
                        : 'Filters out the classical toxic 4-ghati (96 min) interval of each star.'}
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={draftOptions.vishaGhatiFilter}
                    onChange={() => handleToggle('vishaGhatiFilter')}
                    className="mt-1 h-4 w-4 rounded text-astro-primary focus:ring-astro-accent border-astro-border cursor-pointer accent-astro-primary"
                  />
                </label>

                {/* Boost Siddhi Yogas */}
                <label className="flex items-start justify-between gap-3 p-2 rounded-xl bg-astro-card/30 hover:bg-astro-card/70 border border-astro-border/40 cursor-pointer transition-colors">
                  <div className="space-y-0.5">
                    <div className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                      <span>{isHi ? 'सिद्धि योगों को प्राथमिकता (Boost Siddhi Yogas)' : 'Prioritize Auspicious Siddhi Yogas'}</span>
                      <Sparkles className="w-3 h-3 text-amber-500" />
                    </div>
                    <p className="text-[10px] text-astro-secondary-text leading-tight">
                      {isHi 
                        ? 'अमृत सिद्धि, गुरु-पुष्य, रवि-पुष्य व सर्वार्थ सिद्धि को उच्चतम अंक प्रदान करें।' 
                        : 'High weightage for Amrita Siddhi, Guru-Pushya & Sarvartha Siddhi Yogas.'}
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={draftOptions.boostSiddhiYogas}
                    onChange={() => handleToggle('boostSiddhiYogas')}
                    className="mt-1 h-4 w-4 rounded text-astro-primary focus:ring-astro-accent border-astro-border cursor-pointer accent-astro-primary"
                  />
                </label>
              </div>
            </div>
          </div>

          {/* Footer note & action buttons */}
          <div className="pt-2.5 border-t border-astro-border/60 flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-astro-secondary-text">
                {activeDraftCount} {isHi ? 'नियम सक्रिय' : 'rules active'}
              </span>
              {hasPendingChanges && (
                <span className="text-[9px] font-bold px-1.5 py-0.2 rounded bg-amber-100 text-amber-800 border border-amber-300 animate-pulse">
                  {isHi ? 'अपरिवर्तित' : 'Modified'}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => {
                  setDraftOptions(options);
                  setIsOpen(false);
                }}
                className="text-[11px] font-semibold px-2.5 py-1 rounded-lg border border-astro-border text-astro-secondary-text hover:bg-astro-card transition-colors cursor-pointer"
              >
                {isHi ? 'रद्द करें' : 'Cancel'}
              </button>
              <button
                type="button"
                onClick={handleApply}
                className="text-[11px] font-bold px-3.5 py-1 bg-astro-primary text-white rounded-lg hover:bg-astro-secondary transition-all shadow-2xs active:scale-95 cursor-pointer flex items-center gap-1"
              >
                <Check className="w-3 h-3" />
                <span>{isHi ? 'नियम लागू करें' : 'Apply Rules'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
